/*
  FTS DELIVERY ESTIMATOR (v1)
  -----------------------------------------------------------------
  Shopify's /cart/shipping_rates.json rates the CURRENT cart, so to
  price a hypothetical quantity we temporarily mutate the cart:

    1. zero any existing line for this variant (saving it)
    2. add a shadow line tagged _estimator:true
    3. ask for rates (202 = carrier still thinking, so poll)
    4. remove the shadow, restore the original
    5. render

  Tab closed mid-flow leaves a shadow line behind, so crashRecovery()
  sweeps them on next load within a 2 minute window.

  CHANGES FROM THE TANKS-DIRECT ORIGINAL:
   - restore now preserves line item properties. The original re-added
     with {id, quantity} only, which silently destroyed Hulk Product
     Options configurations on FTS. This is the important one.
   - rate names are cleaned before display. Intuitive concatenates
     every matching scenario, producing internal strings like
     "Shipping:ARMDEL + Shipping:KSP680 + Shipping:WD80".
   - picks the cheapest rate rather than blindly rates[0], and shows
     a count when several exist.
   - poll retry is capped (the original teaser retried subscribe()
     forever).
*/
(function () {
  'use strict';

  var POSTCODE_RE = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i;
  var PC_KEY = 'fts_destim_pc';
  var FLOW_KEY = 'fts_destim_flow';
  var POLL_INTERVAL = 500;
  var POLL_MAX = 10;
  var CRASH_TTL = 2 * 60 * 1000;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

/* /cart/shipping_rates.json returns pounds as a decimal string
     ("80.70"), NOT pence. Do not divide. */
  function money(pounds) {
    return '£' + parseFloat(pounds).toFixed(2);
  }

  /* Intuitive joins every matching scenario name with " + ", and the
     names are internal codes. Strip the prefixes, drop pure codes, and
     fall back to "Delivery" if nothing human survives. */
  function cleanRateName(raw) {
    var parts = String(raw || '').split('+');
    var kept = [];
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim().replace(/^Shipping:\s*/i, '').trim();
      if (!p) continue;
      if (/^[A-Z0-9]{2,}$/.test(p)) continue;      // pure code, e.g. KSP680
      if (kept.indexOf(p) === -1) kept.push(p);
    }
    return kept.length ? kept.join(' + ') : 'Delivery';
  }

  function shippingQuery(pc) {
    return (
      'shipping_address%5Bzip%5D=' + encodeURIComponent(pc) +
      '&shipping_address%5Bcountry%5D=GB' +
      '&shipping_address%5Bfirst_name%5D=Estimator' +
      '&shipping_address%5Blast_name%5D=Quote'
    );
  }

  function removeShadowItems(cb) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var idx = -1;
        for (var i = 0; i < items.length; i++) {
          var props = items[i].properties;
          if (props && props._estimator === 'true') { idx = i; break; }
        }
        if (idx === -1) { sessionStorage.removeItem(FLOW_KEY); cb(); return; }
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ line: idx + 1, quantity: 0 })
        })
          .then(function () { sessionStorage.removeItem(FLOW_KEY); cb(); })
          .catch(function () { sessionStorage.removeItem(FLOW_KEY); cb(); });
      })
      .catch(function () { sessionStorage.removeItem(FLOW_KEY); cb(); });
  }

  function crashRecovery() {
    var ts = sessionStorage.getItem(FLOW_KEY);
    if (!ts) return;
    if (Date.now() - parseInt(ts, 10) < CRASH_TTL) {
      removeShadowItems(function () {});
    } else {
      sessionStorage.removeItem(FLOW_KEY);
    }
  }

  function Estimator(root) {
    this.root = root;
    this.variantId = root.dataset.variantId;
    this.pcEl = root.querySelector('input[name="postcode"]');
    this.qtyEl = root.querySelector('input[type="number"]');
    this.btnEl = root.querySelector('.fts-destim__btn');
    this.resultEl = root.querySelector('.fts-destim__result');
    this.helpEl = root.querySelector('.fts-destim__help');
    this.whyEl = root.querySelector('.fts-destim__why');
    this.saved = null;

    if (!this.pcEl || !this.qtyEl || !this.btnEl || !this.resultEl) {
      console.warn('[fts-destim] markup incomplete, not initialising');
      return;
    }

    var prev = sessionStorage.getItem(PC_KEY);
    if (prev) this.pcEl.value = prev;

    this._bind();
    this._subscribeVariant(0);
  }

  Estimator.prototype._bind = function () {
    var self = this;
    self.btnEl.addEventListener('click', function () { self._run(); });
    self.pcEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); self._run(); }
    });
    if (self.helpEl && self.whyEl) {
      self.helpEl.addEventListener('click', function () {
        var open = self.helpEl.getAttribute('aria-expanded') === 'true';
        self.helpEl.setAttribute('aria-expanded', open ? 'false' : 'true');
        self.whyEl.hidden = open;
      });
    }
  };

  /* Dawn's subscribe() comes from pubsub.js and may not be parsed yet.
     Retry, but give up rather than looping forever. */
  Estimator.prototype._subscribeVariant = function (tries) {
    var self = this;
    if (typeof subscribe !== 'function') {
      if (tries > 40) return;
      setTimeout(function () { self._subscribeVariant(tries + 1); }, 100);
      return;
    }
    subscribe('variant-change', function (event) {
      var v = event && event.data && event.data.variant;
      if (!v) return;
      self.variantId = String(v.id);
      self._set('');
    });
  };

  Estimator.prototype._run = function () {
    var pc = (this.pcEl.value || '').trim().toUpperCase();
    var qty = Math.max(1, parseInt(this.qtyEl.value, 10) || 1);

    if (!POSTCODE_RE.test(pc)) {
      this._set(pc.length > 2
        ? '<p class="fts-destim__error">Please enter a valid UK postcode.</p>'
        : '');
      return;
    }

    var self = this;
    self._lock(true);
    sessionStorage.setItem(FLOW_KEY, String(Date.now()));
    self._set('<p class="fts-destim__loading">Checking rates…</p>');

    self._zeroExisting(function () {
      self._addShadow(qty, function () {
        self._fetchRates(pc.replace(/\s+/g, ''), 0);
      });
    });
  };

  /* Save the WHOLE line item, not just quantity — properties included. */
  Estimator.prototype._zeroExisting = function (cb) {
    var self = this;
    self.saved = null;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var found = null;
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          var props = it.properties || {};
          if (String(it.variant_id) === String(self.variantId) && props._estimator !== 'true') {
            found = { line: i + 1, quantity: it.quantity, properties: it.properties || null };
            break;
          }
        }
        if (!found) { cb(); return; }

        self.saved = found;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ line: found.line, quantity: 0 })
        })
          .then(function () { cb(); })
          .catch(function () { self.saved = null; cb(); });
      })
      .catch(function () { cb(); });
  };

  /* Restore with properties intact. Without this, a customer who had
     already added a configured tank loses their Hulk options. */
  Estimator.prototype._restore = function (cb) {
    var self = this;
    if (!self.saved) { cb(); return; }

    var s = self.saved;
    self.saved = null;

    var item = { id: parseInt(self.variantId, 10), quantity: s.quantity };
    if (s.properties && Object.keys(s.properties).length) {
      item.properties = s.properties;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [item] })
    })
      .then(function () { cb(); })
      .catch(function (err) {
        console.error('[fts-destim] restore failed:', err);
        cb();
      });
  };

  Estimator.prototype._addShadow = function (qty, cb) {
    var self = this;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{
          id: parseInt(self.variantId, 10),
          quantity: qty,
          properties: { _estimator: 'true' }
        }]
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add ' + res.status);
        return res.json();
      })
      .then(function () { cb(); })
      .catch(function (err) {
        console.error('[fts-destim] addShadow:', err);
        sessionStorage.removeItem(FLOW_KEY);
        self._restore(function () {
          self._lock(false);
          self._set('<p class="fts-destim__error">Unable to retrieve delivery rates. Please try again.</p>');
        });
      });
  };

  Estimator.prototype._fetchRates = function (pc, attempt) {
    var self = this;
    fetch('/cart/shipping_rates.json?' + shippingQuery(pc), { credentials: 'same-origin' })
      .then(function (res) {
        if (res.status === 202) {
          if (attempt >= POLL_MAX) throw new Error('timeout');
          setTimeout(function () { self._fetchRates(pc, attempt + 1); }, POLL_INTERVAL);
          return null;
        }
        if (res.status === 422) throw new Error('unprocessable');
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (data === null) return;
        var rates = data && data.shipping_rates;
        console.log('[fts-destim] rates:', JSON.stringify(rates));
        self._cleanup(function () { self._render(rates, pc); });
      })
      .catch(function (err) {
        var msg;
        if (err.message === 'timeout') {
          msg = '<p class="fts-destim__error">Rate calculation took too long. Please try again.</p>';
        } else if (err.message === 'unprocessable') {
          msg = '<p class="fts-destim__error">We couldn\'t find rates for this postcode. Please check and try again.</p>';
        } else {
          msg = '<p class="fts-destim__error">Unable to retrieve delivery rates. Please try again.</p>';
        }
        self._cleanup(function () { self._set(msg); });
        console.error('[fts-destim] fetchRates:', err);
      });
  };

  Estimator.prototype._cleanup = function (cb) {
    var self = this;
    removeShadowItems(function () {
      self._restore(function () {
        self._lock(false);
        if (cb) cb();
      });
    });
  };

  Estimator.prototype._render = function (rates, pc) {
    if (!rates || !rates.length) {
      this._set(
        '<p class="fts-destim__rate">' +
        '<span class="fts-destim__rate-name">Delivery</span>' +
        '<span class="fts-destim__rate-price">Calculated at checkout</span></p>'
      );
      return;
    }

    sessionStorage.setItem(PC_KEY, pc);

    /* Cheapest first — rates[0] is whatever order the carrier returned. */
    var sorted = rates.slice().sort(function (a, b) {
      return parseFloat(a.price) - parseFloat(b.price);
    });
    var best = sorted[0];

    var vat = parseFloat(this.root.dataset.vatRate || '20');
    var inc = parseFloat(best.price) * (1 + vat / 100);

    var html =
      '<p class="fts-destim__rate">' +
      '<span class="fts-destim__rate-name">' + esc(cleanRateName(best.name)) + '</span>' +
      '<span class="fts-destim__rate-price">' + esc(money(best.price)) + '</span></p>' +
      '<p class="fts-destim__rate-note">' + esc(money(inc)) + ' inc VAT';

    if (sorted.length > 1) {
      html += ' · ' + sorted.length + ' options at checkout';
    }
    html += '</p>';

    this._set(html);
  };

  Estimator.prototype._lock = function (on) {
    this.root.classList.toggle('fts-destim--loading', on);
    this.btnEl.disabled = on;
    this.pcEl.disabled = on;
    this.qtyEl.disabled = on;
  };

  Estimator.prototype._set = function (html) {
    this.resultEl.innerHTML = html;
  };

  crashRecovery();

  document.querySelectorAll('.fts-destim').forEach(function (el) {
    new Estimator(el);
  });
})();